import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Sun, Moon, Star, ArrowLeft, ChevronDown, ShieldAlert, Sparkles,
  AlertCircle, Compass, Heart, Gift,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingActions from '../components/FloatingActions';
import SEO from '../components/SEO';
import { safeHtml } from '../lib/sanitize';
import RelatedTools from '../components/RelatedTools';
import TopicalCrossLinks from '../components/TopicalCrossLinks';
import { useLang } from '../context/LanguageContext';
import { PLANETS_CONFIG } from '../data/upayPlanetsConfig';

const ICONS = { Sun, Moon, Star };

const FINAL_DISCLAIMER =
  'रत्न धारण करने से पहले विद्वान् वैदिक ज्योतिषीय ब्राह्मण से अपनी जन्म कुंडली का विश्लेषण अवश्य करवाएँ, क्योंकि गलत रत्न धारण करने से लाभ के स्थान पर हानि होने की संभावना होती है।';

/**
 * UpayPlanetPage — single dynamic page that renders any of the 9 planet
 * deep-dive remedy pages based on the :planetId URL param.
 *
 * Designed for Programmatic SEO: each planet page targets distinct long-tail
 * queries (e.g. "शनि उपाय", "राहु को शांत कैसे करें", "केतु महादशा उपाय") and
 * carries its own Article + FAQPage + BreadcrumbList + Speakable JSON-LD.
 */
export default function UpayPlanetPage() {
  const { planetId } = useParams();
  const { lang } = useLang();
  const [openFaq, setOpenFaq] = useState(0);

  const planet = PLANETS_CONFIG.find((p) => p.slug === planetId);
  // Feb 12, 2026 (N3 SEO Fix) — invalid planetId must return proper 404
  // (noindex + prerender-status-code=404), not silently redirect to /upay.
  if (!planet) {
    return (
      <div className="min-h-screen bg-background" data-testid="upay-not-found">
        <SEO title="Upay Not Found" description="This upay page was not found" path={`/upay/${planetId || ''}`} noIndex />
        <Helmet><meta name="prerender-status-code" content="404" /></Helmet>
        <Header />
        <div className="container-custom py-32 text-center">
          <h1 className="font-heading text-3xl text-text-main mb-4">यह उपाय पृष्ठ नहीं मिला</h1>
          <p className="text-text-muted mb-6">The requested upay page does not exist.</p>
          <Link to="/upay" className="text-gold hover:underline">← सभी उपाय देखें</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const Icon = ICONS[planet.icon] || Star;
  const canonicalUrl = `https://viratastro.cloud/upay/${planet.slug}`;

  // Build comprehensive JSON-LD bundle for SEO + GEO
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: `${planet.name} ग्रह उपाय — मन्त्र, रुद्राक्ष, रत्न और दैनिक उपाय`,
      description: planet.seo.description,
      inLanguage: 'hi-IN',
      url: canonicalUrl,
      datePublished: '2026-05-20',
      dateModified: '2026-05-20',
      author: {
        '@type': 'Person',
        name: 'Acharya Vivek Mudgal',
        url: 'https://viratastro.cloud/about',
        description: '33+ वर्षों का पराशरीय वैदिक ज्योतिष अनुभव, फरीदाबाद',
      },
      publisher: {
        '@type': 'Organization',
        name: 'ViratAstro',
        url: 'https://viratastro.cloud',
        logo: {
          '@type': 'ImageObject',
          url: '/media/founder/IMG_9620-removebg-preview.png',
        },
      },
      about: `${planet.nameEn} — Vedic astrology remedies in Hindi`,
      keywords: planet.seo.keywords,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: planet.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'होम', item: 'https://viratastro.cloud/' },
        { '@type': 'ListItem', position: 2, name: 'नवग्रह उपाय', item: 'https://viratastro.cloud/upay' },
        { '@type': 'ListItem', position: 3, name: `${planet.name} उपाय`, item: canonicalUrl },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      url: canonicalUrl,
      inLanguage: 'hi-IN',
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: ['h1', 'h2', '.featured-answer', '.speakable'],
      },
    },
  ];

  // Other planets for internal linking grid
  const otherPlanets = PLANETS_CONFIG.filter((p) => p.slug !== planet.slug);

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={planet.seo.title}
        description={planet.seo.description}
        path={`/upay/${planet.slug}`}
        keywords={planet.seo.keywords}
        breadcrumbs={[
          { name: 'होम', path: '/' },
          { name: 'नवग्रह उपाय', path: '/upay' },
          { name: `${planet.name} उपाय`, path: `/upay/${planet.slug}` },
        ]}
        jsonLd={jsonLd}
      />
      <Header />

      {/* === Hero === */}
      <section className={`relative pt-32 pb-12 overflow-hidden`}>
        <div className={`absolute inset-0 bg-gradient-to-br ${planet.color} opacity-15`} />
        <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/40 to-background" />
        <div className="absolute -top-32 -right-32 w-[460px] h-[460px] rounded-full bg-gold/10 blur-[140px]" />

        <div className="container-custom relative z-10 max-w-4xl">
          <nav className="flex items-center gap-2 text-text-muted text-xs mb-4" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-gold">होम</Link>
            <span>›</span>
            <Link to="/upay" className="hover:text-gold">नवग्रह उपाय</Link>
            <span>›</span>
            <span className="text-gold">{planet.name} उपाय</span>
          </nav>

          <div className="flex items-start gap-4 mb-5">
            <div className={`w-14 h-14 rounded-2xl ${planet.iconBg} flex items-center justify-center shrink-0 border border-gold/20`}>
              <Icon className={`w-7 h-7 ${planet.iconColor}`} />
            </div>
            <div>
              <span className="text-[11px] tracking-[0.3em] uppercase text-gold font-semibold">वैदिक उपाय</span>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-text-main leading-tight">
                <span className="text-gold-gradient">{planet.name}</span> ग्रह उपाय
              </h1>
              <p className="text-text-muted text-sm mt-1">
                {planet.nameEn} • {planet.short} • <span className="text-gold/80">शुभ दिन: {planet.day}</span>
              </p>
            </div>
          </div>

          {/* Featured snippet — voice-search & AEO ready */}
          <div className="featured-answer glass-card p-5 border-gold/30 mt-4" data-testid="featured-answer">
            <p className="text-[11px] tracking-[0.2em] uppercase text-gold/80 font-bold mb-2">शीघ्र उत्तर</p>
            <p className="text-text-main text-base sm:text-lg leading-relaxed"
               dangerouslySetInnerHTML={safeHtml(planet.featuredAnswer.replace(/\*([^*]+)\*/g, '<strong class="text-gold">$1</strong>'))} />
          </div>

          {/* EEAT author block */}
          <div className="flex items-center gap-3 mt-5 text-xs text-text-muted/80" data-testid="author-block">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-gold to-amber-500 flex items-center justify-center text-purple-950 text-xs font-bold">VM</div>
            <div>
              <p className="text-text-main font-medium">आचार्य Vivek Mudgal</p>
              <p>33+ वर्षों का पराशरीय वैदिक ज्योतिष अनुभव • फरीदाबाद, हरियाणा</p>
            </div>
          </div>
        </div>
      </section>

      {/* === Main content === */}
      <section className="section-padding pt-4">
        <div className="container-custom max-w-4xl space-y-8">

          {/* Intro */}
          <article className="speakable">
            <h2 className="font-heading text-3xl text-text-main mb-3">
              {planet.name} ग्रह का परिचय एवं महत्व
            </h2>
            <p className="text-text-main/90 text-base sm:text-lg leading-relaxed">
              {planet.intro}
            </p>
          </article>

          {/* Weakness signs */}
          <article className="glass-card p-6 border-red-500/20">
            <h2 className="font-heading text-2xl text-text-main mb-4 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-400" />
              {planet.name} ग्रह कमजोर होने के लक्षण
            </h2>
            <ul className="space-y-2.5 text-text-main/90 leading-relaxed">
              {planet.weaknessSigns.map((s, i) => (
                <li key={i} className="flex gap-2.5">
                  <span className="text-red-400 mt-1.5 shrink-0">◆</span>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </article>

          {/* Mahadasha effects */}
          <article className="glass-card p-6 border-gold/15">
            <h2 className="font-heading text-2xl text-text-main mb-3 flex items-center gap-2">
              <Compass className="w-5 h-5 text-gold" />
              {planet.name} महादशा / अंतर्दशा का प्रभाव
            </h2>
            <p className="text-text-main/90 leading-relaxed">{planet.dashaEffects}</p>
          </article>

          {/* Practical remedies */}
          <article>
            <h2 className="font-heading text-3xl text-text-main mb-4">
              {planet.name} ग्रह के व्यवहारिक एवं दैनिक जीवन के उपाय
            </h2>
            <p className="text-text-muted text-sm mb-4">ये उपाय daily life में आसानी से किए जा सकते हैं और कोई विशेष खर्च नहीं माँगते।</p>
            <ul className="space-y-3 text-text-main/95 leading-relaxed">
              {planet.practical.map((p, i) => (
                <li key={i} className="flex gap-3 glass-card p-4 border-white/5">
                  <span className="text-gold text-lg shrink-0 mt-0.5">{i + 1}.</span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </article>

          {/* Relationship & karmic */}
          <article className="glass-card p-6 border-pink-500/20">
            <h2 className="font-heading text-2xl text-text-main mb-3 flex items-center gap-2">
              <Heart className="w-5 h-5 text-pink-400" />
              संबंध एवं कर्मगत सुधार
            </h2>
            <p className="text-text-main/90 leading-relaxed">{planet.relationship}</p>
          </article>

          {/* Donation/seva/temple */}
          <article className="glass-card p-6 border-emerald-500/20">
            <h2 className="font-heading text-2xl text-text-main mb-3 flex items-center gap-2">
              <Gift className="w-5 h-5 text-emerald-400" />
              दान, सेवा एवं मन्दिर उपाय
            </h2>
            <p className="text-text-main/90 leading-relaxed">{planet.donation}</p>
          </article>

          {/* Mantra / Rudraksha / Ratna cards */}
          <div className="grid sm:grid-cols-3 gap-4">
            <article className="glass-card p-5 border-gold/25" data-testid="mantra-card">
              <p className="text-[10px] uppercase tracking-[0.25em] text-gold/70 font-bold mb-2">मन्त्र</p>
              <h3 className="font-heading text-lg text-text-main mb-2 leading-relaxed">{planet.mantra}</h3>
              <p className="text-xs text-text-muted italic leading-relaxed">{planet.mantraNote}</p>
            </article>
            <article className="glass-card p-5 border-gold/25" data-testid="rudraksha-card">
              <p className="text-[10px] uppercase tracking-[0.25em] text-gold/70 font-bold mb-2">रुद्राक्ष</p>
              <h3 className="font-heading text-lg text-text-main leading-relaxed">{planet.rudraksh}</h3>
            </article>
            <article className="glass-card p-5 border-gold/25" data-testid="ratna-card">
              <p className="text-[10px] uppercase tracking-[0.25em] text-gold/70 font-bold mb-2">रत्न</p>
              <h3 className="font-heading text-lg text-text-main leading-relaxed">{planet.ratna}</h3>
            </article>
          </div>

          {/* FAQs */}
          <article data-testid="planet-faqs">
            <h2 className="font-heading text-3xl text-text-main mb-2">
              {planet.name} उपाय से सम्बंधित प्रश्न (FAQs)
            </h2>
            <p className="text-text-muted text-sm mb-5">सबसे आम सवालों के सीधे और संक्षिप्त उत्तर।</p>
            <div className="space-y-3">
              {planet.faqs.map((f, i) => {
                const open = openFaq === i;
                return (
                  <div key={i} className="glass-card border-white/10" data-testid={`faq-${i}`}>
                    <button
                      onClick={() => setOpenFaq(open ? -1 : i)}
                      className="w-full flex items-center justify-between gap-3 p-4 text-left"
                      aria-expanded={open}
                    >
                      <h3 className="text-text-main font-medium text-[15px]">{f.q}</h3>
                      <ChevronDown className={`w-4 h-4 text-gold shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
                    </button>
                    {open && (
                      <div className="px-4 pb-4 text-text-muted text-sm leading-relaxed border-t border-white/5 pt-3">
                        {f.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </article>

          {/* CTA — Pucho Guruji */}
          <div className="glass-card p-7 text-center border-gold/40 bg-gradient-to-br from-purple-900/40 to-amber-950/30" data-testid="pucho-cta">
            <Sparkles className="w-7 h-7 text-gold mx-auto mb-2" />
            <h2 className="font-heading text-2xl sm:text-3xl text-text-main mb-2">
              अपनी कुंडली के अनुसार सटीक {planet.name} उपाय जानें
            </h2>
            <p className="text-text-muted text-sm mb-5 max-w-xl mx-auto">
              पूछो Guruji में अपनी जन्म-तिथि के साथ प्रश्न पूछें — आचार्य Vivek जी आपकी कुंडली का विश्लेषण करके आपके लिए विशेष उपाय बताएँगे।
            </p>
            <Link
              to="/poocho-guruji"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-to-r from-gold to-amber-500 text-purple-950 font-bold shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-transform"
              data-testid="pucho-cta-btn"
            >
              पूछो Guruji से प्रश्न पूछें • ₹99 →
            </Link>
          </div>

          {/* Internal linking to other planets */}
          <article data-testid="other-planets">
            <h2 className="font-heading text-2xl text-text-main mb-4">अन्य ग्रहों के उपाय भी पढ़ें</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {otherPlanets.map((p) => {
                const PI = ICONS[p.icon] || Star;
                return (
                  <Link
                    key={p.slug}
                    to={`/upay/${p.slug}`}
                    className="glass-card p-3 border-white/10 hover:border-gold/40 transition-colors flex items-center gap-2"
                    data-testid={`link-${p.slug}`}
                  >
                    <div className={`w-9 h-9 rounded-xl ${p.iconBg} flex items-center justify-center shrink-0`}>
                      <PI className={`w-4 h-4 ${p.iconColor}`} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-text-main font-semibold text-sm">{p.name} उपाय</p>
                      <p className="text-[10px] text-text-muted truncate">{p.day}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
            <div className="text-center mt-4">
              <Link to="/upay" className="text-gold text-sm hover:underline" data-testid="back-master-link">
                ← सभी नवग्रह उपाय एक साथ देखें
              </Link>
            </div>
          </article>

          {/* Disclaimer */}
          <article className="glass-card p-6 border-red-500/30" data-testid="planet-disclaimer">
            <div className="flex items-center gap-2 mb-3">
              <ShieldAlert className="w-5 h-5 text-red-400" />
              <h2 className="font-heading text-xl text-text-main">महत्वपूर्ण सूचना</h2>
            </div>
            <p className="text-text-main/90 leading-relaxed italic">"{FINAL_DISCLAIMER}"</p>
            <p className="text-text-muted text-xs mt-3">
              — आचार्य Vivek Mudgal | 33+ वर्षों का पराशरीय अनुभव | फरीदाबाद, हरियाणा
            </p>
          </article>
        </div>
      </section>

      <RelatedTools exclude={['/upay']} />
      {/* Feb 12, 2026 SEO Fix — Topical cross-links from Upay → FAQ/Blog for
          "Discovered - not indexed" recovery (audit finding #9). */}
      <TopicalCrossLinks hint={planet?.slug || ''} lang="hi" />
      <FloatingActions />
      <Footer />
    </div>
  );
}
